<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { UiText, UiTabs, UiTabsItem, UiIcon, UiButton, UiAside } from "@shopexperts/shared";
import { useClientReviewRequests } from "@/app/composables/reviews/useClientReviewRequests";
import ClientReviewRequestCard from "@/app/components/reviews/ClientReviewRequestCard.vue";
import LeadSubmissionForm from "@/app/components/LeadSubmissionForm.vue";
import PageHeader from "@/app/components/common/PageHeader.vue";
import { useClientAuthStore } from "@/app/stores/clientAuth";
import { usePostHog } from "@/app/composables/usePostHog";
import SuccessCheckmarkIcon from "@/assets/icons/success-chekmark.svg";
import ProjectListContent from "@/v2Projects/components/ProjectListContent.vue";
import { useProjectsList } from "@/v2Projects/composables/useProjectsList";

const clientAuthStore = useClientAuthStore();

// Initialize PostHog tracking
const {
  trackPageView,
  trackDashboardViewed,
  trackDashboardTabChanged,
  trackSubmitRequestClicked,
  identify,
} = usePostHog();

// Track page view and identify user on mount
onMounted(() => {
  trackPageView('Client Overview');
  trackDashboardViewed('latest_projects');
  
  // Identify user if authenticated
  if (clientAuthStore.user?.id) {
    identify(clientAuthStore.user.id, {
      email: clientAuthStore.user.email,
      first_name: clientAuthStore.user.first_name,
      last_name: clientAuthStore.user.last_name,
      user_type: 'client',
    });
  }
});

// Tab state
const activeTab = ref<'latest_projects' | 'review_requests'>('latest_projects');

// Track tab changes
watch(activeTab, (newTab) => {
  trackDashboardTabChanged(newTab);
});

const {
  side,
  clientGroups,
  expertProjects,
  totalCount,
  isLoading: isLoadingProjects,
  error: projectsError,
  refetch: refetchProjects,
} = useProjectsList({ fetchAllPages: true });

// Fetch pending review requests
const { reviewRequests, isLoading: isLoadingReviewRequests, error: reviewRequestsError, refetch: refetchReviewRequests } = useClientReviewRequests('pending');

// Welcome message with user's first name
const userName = computed(() => {
  return clientAuthStore.user?.first_name || 'there';
});

// Aside state for submitting new leads
const asideOpen = ref(false);
const formKey = ref(0);

function openSubmitLeadAside() {
  trackSubmitRequestClicked('client_overview');
  formKey.value++;
  asideOpen.value = true;
}

function handleLeadSubmitSuccess() {
  asideOpen.value = false;
  refetchProjects();
}

const handleReviewSubmitted = () => {
  refetchReviewRequests();
};
</script>

<template>
  <main class="flex-1 p-4 md:p-8 overflow-y-auto bg-secondary font-light w-full">
    <PageHeader
      :welcome-message="userName"
      description="Here's what's happening with your projects and reviews"
    >
      <template #action>
        <UiButton variant="primary" label="Submit a Request" @click="openSubmitLeadAside" show-arrow>
        </UiButton>
      </template>
    </PageHeader>

    <!-- Tabs -->
    <UiTabs v-model="activeTab" default-value="latest_projects">
      <!-- All Projects Tab -->
      <UiTabsItem value="latest_projects" label="All Projects">
        <ProjectListContent
          :side="side"
          :is-loading="isLoadingProjects"
          :error="(projectsError as Error | null)"
          :client-groups="clientGroups"
          :expert-projects="expertProjects"
          :total-count="totalCount"
          empty-title="No projects yet"
          empty-description="Once your requests are matched with experts, your projects and conversations will appear here."
          @retry="refetchProjects"
          @submit="openSubmitLeadAside"
        />
      </UiTabsItem>

      <!-- Review Requests Tab -->
      <UiTabsItem value="review_requests" label="Review Requests">
        <!-- Loading State -->
        <div v-if="isLoadingReviewRequests" class="text-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <UiText variant="p" as="p" class="text-gray-500">Loading review requests...</UiText>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="reviewRequestsError" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load review requests
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600 mb-4">
              {{ (reviewRequestsError as Error).message || 'An error occurred while fetching review requests' }}
            </UiText>
            <UiButton variant="outline" label="Try Again" @click="refetchReviewRequests" />
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="reviewRequests.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div class="w-20 h-20 flex items-center justify-center">
              <SuccessCheckmarkIcon class="w-full h-full" />
            </div>
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-2">
                No pending review requests
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                You'll see review requests from experts here when they ask for feedback.
              </UiText>
            </div>
          </div>
        </div>

        <!-- Review Requests List -->
        <div v-else class="flex flex-col gap-4">
          <ClientReviewRequestCard v-for="request in reviewRequests" :key="request.id" :request="request"
            @review-submitted="handleReviewSubmitted" />
        </div>
      </UiTabsItem>
    </UiTabs>

    <!-- Submit Lead Aside -->
    <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
      <div class="p-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <LeadSubmissionForm :key="formKey" :disable-auto-redirect="true" @success="handleLeadSubmitSuccess">
          <template #close-button>
            <UiButton variant="ghost" shape="square" @click="asideOpen = false">
              <template #before_label>
                <UiIcon name="close" class="w-6 h-6" />
              </template>
            </UiButton>
          </template>
        </LeadSubmissionForm>
      </div>
    </UiAside>
  </main>
</template>
